import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProductFilters from './ProductFilters';
import './GroupedProductListing.css';
import { productGroupsAPI, API_URL } from '../../utils/api';

const GroupedProductListing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [productGroups, setProductGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const getImageUrl = (imgPath) => {
    if (!imgPath) return null;
    if (imgPath.startsWith('http')) return imgPath;
    const baseUrl = API_URL.replace('/api', '');
    if (imgPath.startsWith('/uploads/')) return `${baseUrl}${imgPath}`;
    return `${baseUrl}/api/images/${imgPath}`;
  };

  // Parse filters from URL
  const filters = {
    vendor: searchParams.get('vendor'),
    department: searchParams.get('department'),
    priceMin: searchParams.get('priceMin'),
    priceMax: searchParams.get('priceMax'),
    inStock: searchParams.get('inStock'),
    sort: searchParams.get('sort') || 'featured',
    search: searchParams.get('search'),
  };

  useEffect(() => {
    loadProductGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadProductGroups = async () => {
    setIsLoading(true);
    try {
      const params = {};
      
      // Build params object from filters
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params[key] = filters[key];
        }
      });

      params.limit = 50;
      params.offset = 0;

      const response = await productGroupsAPI.getProductGroups(params);
      setProductGroups(response.data.data);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error loading product groups:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (value === null || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    
    setSearchParams(newParams);
  };

  const handleClearAll = () => {
    setSearchParams({});
  };

  const handleGroupClick = (groupId) => {
    navigate(`/product-group/${groupId}`);
  };

  const formatPrice = (min, max) => {
    if (min === max) {
      return `$${min.toFixed(2)}`;
    }
    return `$${min.toFixed(2)} - $${max.toFixed(2)}`;
  };

  return (
    <div className="grouped-product-listing">
      <div className="listing-container">
        {/* Sidebar Filters */}
        <aside className="filters-sidebar">
          <ProductFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAll}
          />
        </aside>

        {/* Product Grid */}
        <main className="products-main">
          <div className="products-header">
            <h1>All Products</h1>
            <p className="results-count">
              {isLoading ? 'Loading...' : `${totalCount} products found`}
            </p>
          </div>

          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : productGroups.length === 0 ? (
            <div className="empty-state">
              <p>No products found matching your filters.</p>
              <button onClick={handleClearAll} className="btn-clear">
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="product-groups-grid">
              {productGroups.map((group) => (
                <div
                  key={group.group_id}
                  className="product-group-card"
                  onClick={() => handleGroupClick(group.group_id)}
                >
                  {/* Product Image */}
                  <div className="group-image">
                    {(group.hero_image || group.preview_variants[0]?.image_url) ? (
                      <img
                        src={getImageUrl(group.hero_image || group.preview_variants[0].image_url)}
                        alt={group.title}
                      />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                    {group.group_type === 'kit' && (
                      <span className="kit-badge">Kit</span>
                    )}
                    {!group.in_stock && (
                      <span className="out-of-stock-badge">Out of Stock</span>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="group-info">
                    {group.vendor_name && (
                      <p className="group-vendor">{group.vendor_name}</p>
                    )}
                    <h3 className="group-title">{group.title}</h3>
                    
                    <div className="group-price">
                      {formatPrice(group.min_price, group.max_price)}
                    </div>

                    {group.total_variants > 1 && (
                      <p className="group-variants">
                        {group.total_variants} options available
                      </p>
                    )}

                    {/* Variant Preview Pills */}
                    {group.preview_variants.length > 1 && (
                      <div className="variant-preview">
                        {group.preview_variants.slice(0, 5).map((variant) => (
                          <span key={variant.id} className="variant-pill" title={variant.attribute || variant.size}>
                            {variant.attribute || variant.size}
                          </span>
                        ))}
                        {group.total_variants > 5 && (
                          <span className="variant-pill more">
                            +{group.total_variants - 5}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default GroupedProductListing;
